import React, {type ReactNode} from "react";
import {Box, Typography} from "@mui/material";
import {useRightDrawer} from "crqlar-react-common/dist/components/header/RightDrawerContext";
import {useLocation, useNavigate} from "react-router-dom";
import {PageShellDesktopTabs} from "./PageShellDesktopTabs";
import {PageShellMobileTabSelect} from "./PageShellMobileTabSelect";
import {MobileSidebarButton} from "./leftSidebar/MobileSidebar";
import {useCompactDashboardLayout} from "../../utils/responsive.util";
import {useDocumentTitleOverride} from "./DocumentTitleManager";
import {isPosMode} from "../pos/posMode";

export const PAGE_SHELL_BORDER = "1px solid rgb(68 64 56 / 8%)";

export interface TabRoute {
    path: string;
    label: string;
    render: () => React.ReactNode;
    /** Keeps a route reachable while hiding it from tab navigation. */
    hidden?: boolean;
}

interface Props {
    icon: React.ReactElement;
    title: React.ReactNode;
    documentTitle: string;
    tabs: TabRoute[];
    children?: ReactNode;
}

const matchesTabPath = (pathname: string, tabPath: string) =>
    pathname === tabPath || pathname.startsWith(`${tabPath}/`);

export function PageShell({
    icon,
    title,
    documentTitle,
    tabs,
    children,
}: Props) {
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const {openRightDrawer} = useRightDrawer();
    const isCompactLayout = useCompactDashboardLayout();
    // On a Simphony till the module title is wasted height (see `pos/posMode.ts`). Wide layouts drop
    // the whole header row, which holds only the title there; compact layouts keep the row for the
    // menu button and tab select and drop just the title.
    const hideTitle = isPosMode();
    const hasTabs = tabs.length > 0;
    const activeTab = tabs.find(tab => matchesTabPath(pathname, tab.path)) ?? tabs[0];
    const visibleTabs = tabs.filter(tab => !tab.hidden);
    const activeTabLabel = activeTab?.label;
    const titleParts = [documentTitle, activeTabLabel].filter(Boolean);
    useDocumentTitleOverride(titleParts.join(" "));

    if (hasTabs && !activeTab) return null;

    return (
        <Box sx={{
            flex: 1,
            minWidth: 0,
            boxSizing: "border-box",
            p: isCompactLayout ? 0 : 1,
            // Own exactly the viewport height and lay the header / tabs / content out as a
            // flex column. The content area takes the leftover space via `flex: 1` (below)
            // instead of a hard-coded `calc(100dvh - Npx)`, which previously mis-estimated the
            // chrome height by a couple px and pushed the whole page past the viewport — the
            // source of the phantom document scrollbar. `overflow: hidden` guarantees the page
            // itself never scrolls; every tab scrolls internally via RightSidebarRailLayout.
            height: "100dvh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
        }}>
            <Box
                component="header"
                sx={{
                    display: isCompactLayout ? "grid" : hideTitle ? "none" : "flex",
                    gridTemplateColumns: isCompactLayout ? "auto minmax(0, 1fr) auto" : "none",
                    justifyContent: isCompactLayout ? "stretch" : "center",
                    alignItems: "center",
                    gap: isCompactLayout ? 1 : 2,
                    height: isCompactLayout ? 56 : 48,
                    flexShrink: 0,
                    pl: isCompactLayout ? 1 : 0,
                    pr: isCompactLayout && openRightDrawer ? 8 : isCompactLayout ? 1 : 0,
                }}
            >
                <MobileSidebarButton/>

                <Typography
                    variant="h4"
                    noWrap
                    className="hide-on-print"
                    sx={{
                        // `visibility` rather than `display`, so the compact grid keeps its columns.
                        visibility: hideTitle ? "hidden" : undefined,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: isCompactLayout ? "flex-start" : "center",
                        gap: 1,
                        minWidth: 0,
                        fontWeight: 700,
                        fontSize: isCompactLayout ? 20 : undefined,
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            display: isCompactLayout ? "none" : "inline-flex",
                            alignItems: "center",
                        }}
                    >
                        {icon}
                    </Box>
                    {title}
                </Typography>

                {hasTabs && activeTab && (
                    <PageShellMobileTabSelect
                        tabs={visibleTabs}
                        value={activeTab.path}
                        onSelect={navigate}
                        compact={isCompactLayout}
                    />
                )}
            </Box>

            {hasTabs && activeTab && (
                <PageShellDesktopTabs
                    tabs={visibleTabs}
                    value={activeTab.path}
                    compact={isCompactLayout}
                />
            )}

            <Box
                sx={{
                    display: "flex",
                    // Fill the space left after the header (and tabs) instead of subtracting a
                    // hard-coded chrome height from 100dvh. minHeight:0 lets inner scroll
                    // containers (RightSidebarRailLayout, tables, the calendar grid) bound
                    // themselves and scroll internally.
                    flex: 1,
                    minHeight: 0,
                    bgcolor: "#fff",
                    border: isCompactLayout ? 0 : PAGE_SHELL_BORDER,
                    borderTop: PAGE_SHELL_BORDER,
                    borderRadius: isCompactLayout ? 0 : 4,
                    // Clip the rounded content surface without creating a non-scrolling
                    // overflow ancestor, which would prevent descendants from using sticky.
                    overflow: "clip",
                }}
            >
                <Box component="main" sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
                    {activeTab ? activeTab.render() : children}
                </Box>
            </Box>
        </Box>
    );
}
